import { create } from 'zustand';

export type Permission = string; // e.g., "doorpin.device.read"

const PERMISSIONS_STORAGE_KEY = 'user_permissions';

/** Hydrate permissions from localStorage so the sidebar doesn't flicker on refresh */
function loadPersistedPermissions(): Permission[] {
	try {
		const raw = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) return parsed;
		}
	} catch {
		// ignore corrupt data
	}
	return [];
}

interface PermissionStore {
	// State
	permissions: Permission[];
	isLoaded: boolean;

	// Actions
	setPermissions: (permissions: Permission[]) => void;
	hasPermission: (permission: Permission) => boolean;
	hasAnyPermission: (permissions: Permission[]) => boolean;
	hasAllPermissions: (permissions: Permission[]) => boolean;
	reset: () => void;
}

const persisted = loadPersistedPermissions();

export const usePermissionStore = create<PermissionStore>((set, get) => ({
	// Initial state — hydrate from localStorage so menus render correctly before auth/me resolves
	permissions: persisted,
	isLoaded: persisted.length > 0,

	// Actions
	setPermissions: (permissions) => {
		const perms = permissions || [];
		try {
			localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(perms));
		} catch {
			// storage full — non-critical
		}
		set({
			permissions: perms,
			isLoaded: true,
		});
	},

	hasPermission: (permission) => {
		const { permissions = [] } = get();
		// Support wildcard permissions: "doorpin.*" matches "doorpin.device.read"
		return (permissions || []).some((p) => {
			if (p === permission) return true;
			if (p.endsWith('.*')) {
				const prefix = p.slice(0, -2);
				return permission.startsWith(prefix + '.');
			}
			return false;
		});
	},

	hasAnyPermission: (perms) => {
		return (perms || []).some((p) => get().hasPermission(p));
	},

	hasAllPermissions: (perms) => {
		return (perms || []).every((p) => get().hasPermission(p));
	},

	reset: () => {
		try {
			localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
		} catch {
			// non-critical
		}
		set({
			permissions: [],
			isLoaded: false,
		});
	},
}));

// Selectors
export const selectPermissions = (state: PermissionStore) => state.permissions;
export const selectHasPermission = (state: PermissionStore) => state.hasPermission;
export const selectIsPermissionsLoaded = (state: PermissionStore) => state.isLoaded;
